import { useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutStats, milestones, testimonials, contactInfo } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt')?.toString().trim() || '';

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const formDataToSubmit = new FormData();
      for (const [key, value] of formData.entries()) {
        if (key !== 'phone_alt') {
          formDataToSubmit.append(key, value.toString());
        }
      }

      const response = await fetch('https://readdy.ai/api/form/d9pirnmb4qjhggaepv6g', {
        method: 'POST',
        body: formDataToSubmit,
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  }, []);

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91视频在线观看',
    url: 'https://91video.com/',
    description: '高清视频免费播放平台',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: contactInfo.email,
      telephone: contactInfo.phone,
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: contactInfo.address,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="px-4 md:px-6 pb-14 md:pb-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                关于91视频
              </h1>
              <p className="mt-3 text-foreground-500 max-w-2xl mx-auto text-sm md:text-base">
                91视频在线观看成立于2018年，致力于为全球中文用户提供免费、高清、流畅的在线视频播放服务。我们相信，好的内容应该让每个人都能轻松获取。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {aboutStats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-background-100"
                >
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600">
                    {stat.value}
                    <span className="text-lg md:text-xl">{stat.suffix}</span>
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500 mt-1 whitespace-nowrap">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                发展历程
              </h2>
              <p className="text-sm text-foreground-500 mt-1">一路走来，我们从未停下脚步</p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>

              <div className="space-y-6">
                {milestones.map((milestone, index) => (
                  <div
                    key={milestone.year}
                    className={`flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className={`bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 ${
                        index % 2 === 0 ? 'md:mr-4' : 'md:ml-4'
                      }`}>
                        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                          {milestone.title}
                        </h3>
                        <p className="text-sm text-foreground-600 mt-2 leading-relaxed">
                          {milestone.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 md:w-auto">
                      {index % 2 === 1 && <div className="flex-1 md:hidden"></div>}
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-bold text-sm flex-shrink-0">
                        {milestone.year.slice(2)}
                      </div>
                      <span className="text-sm font-semibold text-foreground-700 whitespace-nowrap">{milestone.year}</span>
                      {index % 2 === 0 && <div className="flex-1 md:hidden"></div>}
                    </div>
                    {index % 2 === 1 && <div className="flex-1 hidden md:block"></div>}
                    {index % 2 === 0 && <div className="flex-1 hidden md:block"></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 mt-1">来自真实用户的评价与信赖</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200/70">
                    <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-semibold text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-950">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mt-1">有任何问题或建议？我们很乐意倾听</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">电子邮箱</h3>
                      <a href={`mailto:${contactInfo.email}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">{contactInfo.email}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">客服电话</h3>
                      <a href={`tel:${contactInfo.phone}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">{contactInfo.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">公司地址</h3>
                      <p className="text-sm text-foreground-600">{contactInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">工作时间</h3>
                      <p className="text-sm text-foreground-600">{contactInfo.workingHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6 space-y-4"
                >
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">发送消息</h3>

                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1">主题</label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入消息主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>

                  <div className="honeypot-field">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-accent-50 text-accent-700 text-sm">
                      <i className="ri-check-line mr-1"></i>
                      消息已发送！感谢您的反馈，我们会在24小时内回复。
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="p-3 rounded-lg bg-primary-50 text-primary-700 text-sm">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line mr-1.5"></i>
                    发送消息
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}